import Navbar from "../components/NavBars/Navbar";
import React, { useEffect, useState, createContext } from "react";
import { useNavigate, Routes, Route, useLocation, Outlet } from "react-router-dom";
import SideNavbar from "../components/NavBars/SideNavbar";
import Footer from "../components/Footer";
import PatientHeader from "../components/Headers/PatientHeader";
import PatientNavbar from "../components/NavBars/PatientNavbar";
import "chartjs-adapter-date-fns";
import Global from "../utils/Global";
import display_types from "../config/display_types";

// Create a context to share common data between tabs
export const SubjectDataContext = createContext();

export default function SubjectRoot() {
  // Common state shared by multiple tabs
  const [isLoading, setIsLoading] = useState(true);
  const [subject, setSubject] = useState(null);
  const [norm, setNorm] = useState(undefined);
  const [refreshData, setRefreshData] = useState(false);
  const display_type = Global.localAppHandler.user.display_type;
  const location = useLocation();

  let navigate = useNavigate();

  // Helper function to determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;
    return {
      overview: path.includes('/overview') || path.endsWith('/'),
      trials: path.includes('/trials'),
      detailed: path.includes('/detailed'),
      gait_metrics: path.includes('/metrics'),
      reports: path.includes('/reports'),
      patientDetails: path.includes('/patientDetails')
    };
  };

  // Handle common data fetching for all tabs
  useEffect(() => {
    const fetchCommonData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch the base data that's needed by multiple tabs
        const [subject, norm] = await Promise.all([
          Global.localAppHandler.app_get_subject(),
          Global.localAppHandler.app_retrieve_normal_ranges()
        ]);
        
        setSubject(subject);
        setNorm(norm);
      } catch (error) {
        console.error("Error fetching common subject data:", error);
        setSubject(null);
        navigate("/dashboard");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCommonData();
  }, [navigate, refreshData]);

  // Handle body class
  useEffect(() => {
    document.body.classList.add("nav-fixed");
    
    return () => {
      document.body.classList.remove("nav-fixed");
    };
  }, []);

  // Create the context value with common shared data
  const contextValue = {
    isLoading,
    setIsLoading,
    subject,
    norm,
    refreshData,
    setRefreshData,
    display_type
  };

  const activeTab = getActiveTab();
  const shouldShowNavbar = !location.pathname.includes('/trials/');

  // Render loading state or main content
  return (
    <>
      <Navbar />
      <div id="layoutSidenav">
        <SideNavbar
          patientSubHeaderEnabled={true}
          activeTab={{ studyOverview: false, subjects: true, manageTeam: false, reviewReports: false, remoteAlerts: false }}
        />
        <div id="layoutSidenav_content">
          <main>
            <PatientHeader
              bg={"bg-white"}
              subjectName={
                display_type === display_types.PHI
                  ? subject?.first_name && subject?.last_name
                    ? `${subject.first_name} ${subject.last_name}`
                    : ""
                  : subject?.name
              }
              subheader={subject?.dob ? `Date of Birth: ${subject.dob}` : ""}
            />
            
            {/* Patient navbar for tab navigation */}
            {shouldShowNavbar && (
              <div className="container-xl mt-n10">
                <PatientNavbar activeTab={activeTab} />
              </div>
            )}
            
            {/* Context Provider wrapping the content */}
            <SubjectDataContext.Provider value={contextValue}>
  <div className="content-container" style={{ minHeight: "500px" }}>
    {isLoading ? (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "300px", marginTop: "100px" }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading patient data...</p>
        </div>
      </div>
    ) : (
      <Outlet />
    )}
  </div>
</SubjectDataContext.Provider>
          </main>

          <Footer light={true} />
        </div>
      </div>
    </>
  );
}